/**
 * Calendar link generation utilities
 * Generates URLs for adding events to Google Calendar and Office 365
 */

/**
 * Format date for calendar URLs
 * @param {string|Date} date - ISO date string or Date object
 * @param {string} type - Calendar type: 'google' or 'outlook'
 * @returns {string} Formatted date string
 * - Google: YYYYMMDDTHHmmssZ (UTC format without separators)
 * - Outlook: YYYY-MM-DDTHH:mm:ssZ (UTC ISO format)
 */
function formatCalendarDate(date, type) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  // Get UTC components
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  const seconds = String(d.getUTCSeconds()).padStart(2, '0');

  if (type === 'google') {
    // Google Calendar: UTC format without separators (YYYYMMDDTHHmmssZ)
    return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
  }

  // Outlook: ISO 8601 UTC format (YYYY-MM-DDTHH:mm:ssZ)
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

/**
 * Generate Google Calendar URL
 * Google Calendar uses UTC dates and requires the ctz parameter for timezone
 * @param {Object} options - Event options
 * @param {string} options.title - Event title
 * @param {string} options.start - Start date/time (ISO string)
 * @param {string} options.end - End date/time (ISO string)
 * @param {string} [options.description] - Event description
 * @param {string} [options.location] - Event location
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarUrl({ title, start, end, description = '', location = '' }) {
  const startDate = formatCalendarDate(start, 'google');
  const endDate = formatCalendarDate(end, 'google');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${startDate}/${endDate}`,
    details: description,
    location: location,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Office 365 Calendar URL
 * @param {Object} options - Event options
 * @param {string} options.title - Event title
 * @param {string} options.start - Start date/time (ISO string in UTC)
 * @param {string} options.end - End date/time (ISO string in UTC)
 * @param {string} [options.description] - Event description
 * @param {string} [options.location] - Event location
 * @returns {string} Office 365 Calendar URL
 */
export function generateOffice365CalendarUrl({
  title,
  start,
  end,
  description = '',
  location = '',
}) {
  const startDate = formatCalendarDate(start, 'outlook');
  const endDate = formatCalendarDate(end, 'outlook');

  const params = new URLSearchParams({
    subject: title,
    startdt: startDate,
    enddt: endDate,
    body: description,
    location: location,
    path: '/calendar/action/compose',
    rru: 'addevent',
  });

  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
}
